import classnames from 'classnames/bind'
import { useMemo, useState } from 'react'

import {
  Accordion,
  AccordionPanel,
} from '@unlikelystudio/react-abstract-components'
import { useGetCustomerOrders } from '@unlikelystudio/react-ecommerce-hooks'

import EmptySection from '~/components/Account/Logged/EmptySection'
import LoadingSection from '~/components/Account/Logged/LoadingSection'
import OrderDetails from '~/components/Account/Logged/Orders/OrderDetails'
import OrderHead from '~/components/Account/Logged/Orders/OrderHead'
import { OrdersProps } from '~/components/Account/Logged/Orders/types'
import SectionTitle from '~/components/Account/Logged/SectionTitle'

import useGetAdminOrder from '~/hooks/account/useGetAdminOrder'
import useLocale from '~/hooks/useLocale'

import serializeOrder from '~/data/serialize-order'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export function Orders({
  className,
  emptyTitle,
  emptyLink,
  title,
  orders,
  isLoading,
}: OrdersProps) {
  const [orderId, setOrderId] = useState<string>(null)
  const hasOrders = orders?.length > 0

  const { data: adminOrder, isLoading: adminOrderIsLoading } =
    useGetAdminOrder(orderId)

  return (
    <div className={cx(css.Orders, className)}>
      <SectionTitle title={title} />
      {isLoading ? (
        <LoadingSection />
      ) : hasOrders ? (
        <>
          <Accordion className={cx(css.accordion)}>
            {orders?.map(({ id, ...order }, index) => (
              <AccordionPanel
                key={`orderRow${id}_${index}`}
                index={index}
                head={(isActive: boolean) => {
                  if (isActive) {
                    setOrderId(id)
                  }

                  return (
                    <OrderHead
                      className={css.row}
                      isActive={isActive}
                      {...order}
                    />
                  )
                }}
                body={
                  <OrderDetails
                    {...order}
                    className={css.orderDetails}
                    paymentDetails={adminOrder?.paymentDetails}
                    isLoading={adminOrderIsLoading}
                  />
                }
              />
            ))}
          </Accordion>
        </>
      ) : (
        <>
          <EmptySection title={emptyTitle} link={emptyLink} />
        </>
      )}
    </div>
  )
}

Orders.defaultProps = {}

export default function OrdersWrapper(props: Omit<OrdersProps, 'orders'>) {
  const locale = useLocale()
  const { data, isLoading } = useGetCustomerOrders({ ordersAmount: 20 })
  const orders = useMemo(() => {
    return data?.pages?.[0]?.map((order) => serializeOrder({ locale, order }))
  }, [data])

  return <Orders isLoading={isLoading} orders={orders} {...props} />
}
